import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  restaurantId: string | null;
  networkId: string | null;
  restaurant?: {
    id: string;
    name: string;
    slug: string;
    logoUrl?: string | null;
  };
  network?: {
    id: string;
    name: string;
    slug: string;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_STORAGE_KEY = 'olso_token';
const AUTH_CHANNEL = 'olso-auth';

function getInitialToken(): string | null {
  if (typeof window === 'undefined') return null;
  const urlToken = new URLSearchParams(window.location.search).get('_auth');
  if (urlToken) return urlToken;
  return localStorage.getItem(AUTH_STORAGE_KEY);
}

function getApiUrl(): string {
  return API_URL;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(getInitialToken);
  const [loading, setLoading] = useState(true);
  const [apiUrl, setApiUrl] = useState(getApiUrl);

  // Load runtime config (config.json has API_URL from build - fallback if env was wrong)
  useEffect(() => {
    fetch('/config.json')
      .then((r) => r.json())
      .then((c) => {
        if (c?.API_URL) setApiUrl(c.API_URL);
      })
      .catch(() => {});
  }, []);

  // Load and verify token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('_auth');

    const applyToken = (t: string) => {
      localStorage.setItem(AUTH_STORAGE_KEY, t);
      setToken(t);
      verifyToken(t);
    };

    if (urlToken) {
      params.delete('_auth');
      const newSearch = params.toString();
      const newPath = window.location.pathname + (newSearch ? '?' + newSearch : '');
      window.history.replaceState({}, '', newPath);
      applyToken(urlToken);
      return;
    }

    if (token) {
      verifyToken(token);
      return;
    }

    // Fallback: request auth via BroadcastChannel (kitchen opened from admin)
    try {
      const bc = new BroadcastChannel(AUTH_CHANNEL);
      bc.postMessage({ type: 'AUTH_REQUEST' });
      const timeoutId = setTimeout(() => {
        bc.close();
        setLoading(false);
      }, 3000);
      const handler = (e: MessageEvent) => {
        if (e.data?.type === 'AUTH_RESPONSE' && e.data?.token) {
          clearTimeout(timeoutId);
          applyToken(e.data.token);
          bc.close();
        }
      };
      bc.onmessage = handler;
      return () => {
        clearTimeout(timeoutId);
        bc.close();
      };
    } catch {
      setLoading(false);
    }
  }, [apiUrl]);

  // Respond to auth requests from other tabs (admin sends token to kitchen)
  useEffect(() => {
    if (!token) return;
    try {
      const bc = new BroadcastChannel(AUTH_CHANNEL);
      bc.onmessage = (e: MessageEvent) => {
        if (e.data?.type === 'AUTH_REQUEST') {
          bc.postMessage({ type: 'AUTH_RESPONSE', token });
        }
      };
      return () => bc.close();
    } catch {
      // BroadcastChannel not supported
    }
  }, [token]);

  // Sync auth across tabs - only when another tab logs IN (new token)
  // Ignore logout (null) - prevents one tab's verify failure from logging out others
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_STORAGE_KEY || !e.newValue) return;
      const newToken = e.newValue;
      if (newToken === token) return; // Already have it
      setToken(newToken);
      verifyToken(newToken);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [token]);


  // Set up axios interceptor to include token in requests
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Handle 401 errors (unauthorized) - but only for auth endpoints
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only handle 401 for auth endpoints, not for public routes
        if (error.response?.status === 401 && error.config?.url?.includes('/api/auth/')) {
          // Token expired or invalid - clear state only, not localStorage
          // (clearing localStorage would trigger storage event and log out other tabs)
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await axios.get(`${apiUrl}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${tokenToVerify}`,
        },
      });
      setUser(response.data.user);
    } catch (error: any) {
      // Token invalid - clear state only, don't touch localStorage
      // (clearing localStorage would trigger storage event and log out other tabs)
      console.log('Token verification failed');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${apiUrl}/api/auth/login`, {
        email,
        password,
      });

      const { user: userData, token: newToken } = response.data;
      if (!userData || !newToken) {
        throw new Error('Invalid response from server');
      }

      setUser(userData);
      setToken(newToken);
      localStorage.setItem(AUTH_STORAGE_KEY, newToken);
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        throw new Error(
          'Cannot reach server. Set VITE_API_URL in Railway frontend variables to your backend URL (e.g. https://your-backend.up.railway.app)'
        );
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password');
      }
      if (error.response?.status === 0) {
        throw new Error('CORS or connection blocked. Set FRONTEND_URL in backend to your frontend URL.');
      }
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

